import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Users, Shield, Plus, Edit, Trash2, Key } from 'lucide-react';
import api from '@/lib/api';
import { useToast } from '@/hooks/use-toast';

export default function StaffManagement() {
  const [roles, setRoles] = useState([]);
  const [staff, setStaff] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [showStaffDialog, setShowStaffDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [editingStaff, setEditingStaff] = useState(null);
  const { toast } = useToast();

  // Form states
  const [roleForm, setRoleForm] = useState({ name: '', description: '', permissions: [] });
  const [staffForm, setStaffForm] = useState({ name: '', email: '', password: '', role_id: '' });
  const [savingRole, setSavingRole] = useState(false);
  const [savingStaff, setSavingStaff] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rolesRes, staffRes, permsRes] = await Promise.all([
        api.get('/tenant/roles'),
        api.get('/tenant/staff'),
        api.get('/tenant/permissions')
      ]);
      setRoles(rolesRes.data.roles || []);
      setStaff(staffRes.data.staff || []);
      setPermissions(permsRes.data.by_category || {});
    } catch (err) {
      toast({ title: 'Error', description: 'Gagal memuat data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRole = async () => {
    if (!editingRole && !roleForm.name.trim()) {
      toast({ title: 'Error', description: 'Nama role wajib diisi', variant: 'destructive' });
      return;
    }
    if (editingRole && !editingRole.id) {
      toast({ title: 'Error', description: 'Role tidak valid', variant: 'destructive' });
      return;
    }
    setSavingRole(true);
    try {
      if (editingRole) {
        const payload = editingRole.is_system
          ? { description: roleForm.description, permissions: roleForm.permissions }
          : roleForm;
        await api.put(`/tenant/roles/${editingRole.id}`, payload);
        toast({ title: 'Berhasil', description: 'Role berhasil diupdate' });
      } else {
        await api.post('/tenant/roles', roleForm);
        toast({ title: 'Berhasil', description: 'Role berhasil dibuat' });
      }
      setShowRoleDialog(false);
      setEditingRole(null);
      setRoleForm({ name: '', description: '', permissions: [] });
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Gagal menyimpan role', variant: 'destructive' });
    } finally {
      setSavingRole(false);
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!confirm('Yakin ingin menghapus role ini?')) return;
    try {
      await api.delete(`/tenant/roles/${roleId}`);
      toast({ title: 'Berhasil', description: 'Role berhasil dihapus' });
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Gagal menghapus role', variant: 'destructive' });
    }
  };

  const handleSaveStaff = async () => {
    try {
      if (editingStaff) {
        await api.put(`/tenant/staff/${editingStaff.id}`, {
          name: staffForm.name,
          role_id: staffForm.role_id,
          is_active: staffForm.is_active
        });
        toast({ title: 'Berhasil', description: 'Staff berhasil diupdate' });
      } else {
        await api.post('/tenant/staff', staffForm);
        toast({ title: 'Berhasil', description: 'Staff berhasil ditambahkan' });
      }
      setShowStaffDialog(false);
      setEditingStaff(null);
      setStaffForm({ name: '', email: '', password: '', role_id: '' });
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Gagal menyimpan staff', variant: 'destructive' });
    }
  };

  const handleDeleteStaff = async (staffId) => {
    if (!confirm('Yakin ingin menghapus staff ini?')) return;
    try {
      await api.delete(`/tenant/staff/${staffId}`);
      toast({ title: 'Berhasil', description: 'Staff berhasil dihapus' });
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Gagal menghapus staff', variant: 'destructive' });
    }
  };

  const togglePermission = (code) => {
    setRoleForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(code)
        ? prev.permissions.filter(p => p !== code)
        : [...prev.permissions, code]
    }));
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="staff-management-page">
      <div>
        <h1 className="text-2xl font-bold">Manajemen Staff & Role</h1>
        <p className="text-muted-foreground">Kelola akses staff ke dashboard tenant</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Staff</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{staff.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Role</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{roles.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Staff Aktif</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{staff.filter(s => s.is_active).length}</div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="staff">
        <TabsList>
          <TabsTrigger value="staff" data-testid="tab-staff">
            <Users className="h-4 w-4 mr-2" /> Staff
          </TabsTrigger>
          <TabsTrigger value="roles" data-testid="tab-roles">
            <Shield className="h-4 w-4 mr-2" /> Roles
          </TabsTrigger>
        </TabsList>

        <TabsContent value="staff" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => { setEditingStaff(null); setStaffForm({ name: '', email: '', password: '', role_id: '' }); setShowStaffDialog(true); }} data-testid="add-staff-btn">
              <Plus className="h-4 w-4 mr-2" /> Tambah Staff
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-medium">Nama</th>
                    <th className="text-left p-4 font-medium">Email</th>
                    <th className="text-left p-4 font-medium">Role</th>
                    <th className="text-left p-4 font-medium">Status</th>
                    <th className="text-right p-4 font-medium">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {staff.map(s => (
                    <tr key={s.id} className="border-t" data-testid={`staff-row-${s.id}`}>
                      <td className="p-4">{s.name}</td>
                      <td className="p-4">{s.email}</td>
                      <td className="p-4">
                        <Badge variant="outline">{s.role_name}</Badge>
                      </td>
                      <td className="p-4">
                        <Badge variant={s.is_active ? "default" : "secondary"}>
                          {s.is_active ? 'Aktif' : 'Nonaktif'}
                        </Badge>
                      </td>
                      <td className="p-4 text-right">
                        <Button variant="ghost" size="icon" onClick={() => { setEditingStaff(s); setStaffForm({ name: s.name, email: s.email, role_id: s.role_id, is_active: s.is_active }); setShowStaffDialog(true); }}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteStaff(s.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {staff.length === 0 && (
                    <tr>
                      <td colSpan={5} className="p-4 text-center text-muted-foreground">
                        Belum ada staff. Klik "Tambah Staff" untuk menambahkan.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="roles" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => { setEditingRole(null); setRoleForm({ name: '', description: '', permissions: [] }); setShowRoleDialog(true); }} data-testid="add-role-btn">
              <Plus className="h-4 w-4 mr-2" /> Tambah Role
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {roles.map(role => (
              <Card key={role.id} data-testid={`role-card-${role.id}`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{role.name}</CardTitle>
                    {role.is_system && <Badge variant="secondary">Sistem</Badge>}
                  </div>
                  <CardDescription>{role.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Key className="h-4 w-4" />
                    <span>{role.permissions?.length || 0} permissions</span>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => { setEditingRole(role); setRoleForm({ name: role.name, description: role.description, permissions: role.permissions || [] }); setShowRoleDialog(true); }}>
                      <Edit className="h-3 w-3 mr-1" /> Edit
                    </Button>
                    {!role.is_system && (
                      <Button variant="outline" size="sm" onClick={() => handleDeleteRole(role.id)}>
                        <Trash2 className="h-3 w-3 text-red-500" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Staff Dialog */}
      <Dialog open={showStaffDialog} onOpenChange={setShowStaffDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingStaff ? 'Edit Staff' : 'Tambah Staff'}</DialogTitle>
            <DialogDescription>
              {editingStaff ? 'Update informasi staff' : 'Tambahkan staff baru ke tim Anda'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nama</Label>
              <Input value={staffForm.name} onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })} />
            </div>
            {!editingStaff && (
              <>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={staffForm.email} onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })} />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input type="password" value={staffForm.password} onChange={(e) => setStaffForm({ ...staffForm, password: e.target.value })} />
                </div>
              </>
            )}
            <div>
              <Label>Role</Label>
              <Select value={staffForm.role_id || ''} onValueChange={(v) => setStaffForm({ ...staffForm, role_id: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Pilih role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map(role => (
                    <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStaffDialog(false)}>Batal</Button>
            <Button onClick={handleSaveStaff}>Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role Dialog */}
      <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRole ? 'Edit Role' : 'Tambah Role'}</DialogTitle>
            <DialogDescription>
              {editingRole ? 'Update permissions untuk role ini' : 'Buat role baru dengan permissions yang sesuai'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nama Role</Label>
              <Input value={roleForm.name} onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })} disabled={editingRole?.is_system} />
            </div>
            <div>
              <Label>Deskripsi</Label>
              <Input value={roleForm.description} onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })} />
            </div>
            <div>
              <Label>Permissions</Label>
              <div className="mt-2 space-y-4 max-h-[300px] overflow-y-auto border rounded-lg p-4">
                {Object.entries(permissions).map(([category, perms]) => (
                  <div key={category}>
                    <h4 className="font-medium text-sm mb-2">{category}</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {perms.map(perm => (
                        <label key={perm.code} className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox 
                            checked={roleForm.permissions.includes(perm.code)} 
                            onCheckedChange={() => togglePermission(perm.code)}
                          />
                          {perm.name}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowRoleDialog(false)}>Batal</Button>
            <Button type="button" onClick={handleSaveRole} disabled={savingRole}>
              {savingRole ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
